import type {PunishmentDetailSerializer} from "@/generated/Campaign";
import type {PunishmentSerializer} from "@/generated/Punishment";
import {INITIAL_TEMPLATE_STATE} from "@/store/constants";
import type {
  TBundle,
  TCampaignType,
  TDisciplineCampaign,
  TDisciplineTab,
  TFloatingBuyouts,
  TInstagramContent,
  TInvoice,
  TInvoiceTab,
  TLanguage,
  TNormalInfluencerTab,
  TPermission,
  TProInfluencerTab,
  TTemplateCustomContent,
  TTemplateForm,
} from "@/store/types";
import {atom} from "jotai";
import {atomWithImmer, withImmer} from "jotai-immer";
import {atomWithLocation} from "jotai-location";
import {atomWithReset, atomWithStorage} from "jotai/utils";

const INITIAL_TEMPLATE_CONTENT_STATE: TTemplateCustomContent = {
  feedback: "",
  subject: "",
  template_id: null,
  template_name: "",
};

type TContentReviewCustomContent = TTemplateCustomContent & {
  featured?: boolean | null;
  quality_score?: number | null;
};
export type TFeedbackLanguage = "en_EN" | "de_CH";

export const contentReviewCustomContentAtom = withImmer(
  atomWithStorage<TContentReviewCustomContent>("contentReview", {
    ...INITIAL_TEMPLATE_CONTENT_STATE,
    featured: null,
    quality_score: null,
  }),
);
export const contentReviewTemplateFormAtom = withImmer(
  atomWithStorage<TTemplateForm>("adminFeedbackTemplate", INITIAL_TEMPLATE_STATE),
);
export const disciplinaryModalAtom = withImmer(
  atom<{
    campaignApplication?: TDisciplineCampaign;
    disciplineDuration?: number;
    influencer: {
      firstName?: string;
      id: number | null;
      image?: string | null;
      isInstagramVerified?: boolean;
      isTiktokVerified?: boolean;
      isUgcVerified?: boolean;
      username?: string | null;
    };
    instagramDiscipline?: PunishmentSerializer | null;
    isTimeToggleEnabled?: boolean;
    lastDiscipline?: PunishmentSerializer | null;
    selectedDiscipline?: PunishmentSerializer | PunishmentDetailSerializer | null;
    selectedUnverificationType?: TCampaignType;
    tiktokDiscipline?: PunishmentSerializer | null;
    ugcDiscipline?: PunishmentSerializer | null;
    userId?: number;
  }>({
    campaignApplication: undefined,
    disciplineDuration: 0,
    influencer: {
      id: null,
      firstName: "",
      image: "",
      isInstagramVerified: false,
      isTiktokVerified: false,
      isUgcVerified: false,
      username: null,
    },
    instagramDiscipline: null,
    isTimeToggleEnabled: false,
    lastDiscipline: null,
    selectedDiscipline: null,
    selectedUnverificationType: undefined,
    tiktokDiscipline: null,
    ugcDiscipline: null,
    userId: undefined,
  }),
);
export const disciplineCustomContentAtom = withImmer(
  atomWithStorage<TTemplateCustomContent>("templateDisciplineContent", INITIAL_TEMPLATE_CONTENT_STATE),
);
export const disciplineTemplateFormAtom = withImmer(
  atomWithStorage<TTemplateForm>("templateDisciplineForm", {
    ...INITIAL_TEMPLATE_STATE,
    punishment_type: "PERMANENT_BAN",
  }),
);
export const locationAtom = atom({previousRoute: "", currentRoute: ""});
export const locationWithParamsAtom = atomWithLocation();
export const languagePreferenceAtom = atomWithStorage<TLanguage | null>("languagePreference", null);
export const latestActiveCampaignForInviteAtom = atomWithStorage<number | null>("latestActiveCampaignForInvite", null);
export const shouldMuteVideosAtom = atomWithStorage<boolean>("shouldMuteVideos", false);
export const campaignIncidiesAtom = atomWithImmer<{
  active: number;
  open: number;
  complete: number;
  paused: number;
}>({
  active: 1,
  open: 1,
  complete: 1,
  paused: 1,
});
export const campaignDeleteAtom = atom<{id?: number; draft?: boolean} | null | "ALL_DRAFTS">(null);

export const isFloatingBuyoutMenuVisibleAtom = atom<boolean>(false);
export const isDraggingSlider = atom<boolean>(false);
export const hasStartedCampaignCreationAtom = atom<boolean>(false);
export const groupDeleteAtom = atom<number | null>(null);
export const lastSelectedGroupIdAtom = atom<number | null>(null);
export const lastModifiedGroupInfluencerIdAtom = atom<number | null>(null);
export const showPartnershipAdsTutorialCtaAtom = atom<boolean>(true);
export const showSparkAdsTutorialCtaAtom = atom<boolean>(true);
export const scrollSectionAtom = atom<string | null>(null);
export const editedPaymentMethodIdAtom = atom<string | null>(null);
export const adminFeedbackLatestIndexAtom = atomWithStorage<number>("adminFeedbackLatestIndex", 0);
export const selectedCompanyIdAtom = atom<number | null>(null);
const inviteStatePrimitveAtom = atomWithReset<{
  email: string;
  permission: TPermission;
}>({
  email: "",
  permission: "WRITE",
});
export const inviteStateAtom = withImmer(inviteStatePrimitveAtom);

// Campaign fields atom with versioning system
export const campaignFieldsAtom = withImmer(
  atomWithStorage<{
    brand_does: {value: string; history: string[]; currentHistoryIndex: number};
    campaign_description: {value: string; history: string[]; currentHistoryIndex: number};
    task_description: {value: string; history: string[]; currentHistoryIndex: number};
  }>("campaignFields", {
    brand_does: {value: "", history: [], currentHistoryIndex: -1},
    campaign_description: {value: "", history: [], currentHistoryIndex: -1},
    task_description: {value: "", history: [], currentHistoryIndex: -1},
  }),
);
export const cancelApplicationModalDataAtom = atom<{
  application: {id?: number; product_sent?: boolean; voucher?: string | null};
  campaign: {
    id?: number;
    service_is_physical_product?: boolean;
    service_is_voucher?: boolean;
    title?: string | null;
    ugc?: boolean;
  };
  influencer: {first_name?: string | null; picture?: string | null; picture320?: string | null};
} | null>(null);
export const createCampaignForCompanyAtom = atom<number | null>(null);
export const createCustomerModalOpenAtom = atom<boolean>(false);
export const disciplineTabAtom = atom<TDisciplineTab>("UNVERIFY");
export const editCompanyMemberModalAtom = atom<{
  isVisible: boolean;
  isRegistering: boolean;
}>({isVisible: false, isRegistering: false});
export const extendCampaignModalDataAtom = atom<{
  application?: {completed?: string | null; id?: number; first_name?: string | null};
  campaign?: {id?: number; on_going?: boolean; ugc?: boolean; completed?: string | null};
} | null>(null);
export const hasClosedPaymentFailedModalAtom = atomWithStorage<boolean>("hasClosedPaymentFailedModal", false);
export const hasJustRegisteredAtom = atom<boolean>(false);
export const invoiceTabStateAtom = atom<TInvoiceTab>("campaigns_and_plans");
export const isAdminFeedbackModalOpenAtom = atom<boolean>(false);
export const isAgencyInviteModalOpenAtom = atom<boolean>(false);
export const isDeleteTemplateModalOpenAtom = atom(false);
export const isDisciplinaryModalOpenAtom = atom(false);
export const isFinishRegistrationModalVisibleAtom = atom<boolean>(false);
const isMoreBuyoutsModalOpenPrimitiveAtom = atomWithReset<{
  show: boolean;
  type: TBundle;
}>({show: false, type: "EXTRA_BUYOUTS"});
export const isMoreBuyoutsModalOpenAtom = withImmer(isMoreBuyoutsModalOpenPrimitiveAtom);
export const isMoreInviteModalOpenAtom = atom<boolean>(false);
const isRequestMoreResourcesModalOpenPrimitveAtom = atomWithReset<{
  show: boolean;
  type: TFloatingBuyouts;
}>({show: false, type: "buyouts"});
export const isRequestMoreResourcesModalOpenAtom = withImmer(isRequestMoreResourcesModalOpenPrimitveAtom);
export const isRequestPermissionsModalOpenAtom = atom<boolean>(false);
export const isSingleDisciplineModalOpenAtom = atom(false);
export const isTriggeredFromDisciplinaryModalAtom = atom(false);
export const isWelcomeToMultiAccountModalOpenAtom = atom<boolean>(false);
export const multiAccountInviteModalAtom = atom<{
  isVisible: boolean;
  isViewingOwnCompany: boolean;
}>({
  isVisible: false,
  isViewingOwnCompany: false,
});
export const normalInfluencerTabAtom = atom<TNormalInfluencerTab>("open_applications");
export const paymentFailedModalDataAtom = atom<TInvoice[] | null>(null);
export const purchaseModalContentAtom = atom<{
  buyoutsPerContent: number;
  contents: TInstagramContent[];
  onPurchased?: () => void;
}>({buyoutsPerContent: 1, contents: []});
export const proInfluencerTabAtom = atom<TProInfluencerTab>("open_applications_pro");
export const showProInfluencersContentBuyoutInfoAtom = atomWithStorage<boolean>(
  "showProInfluencersContentBuyoutInfo",
  true,
);
export const templateLanguageAtom = atom<TFeedbackLanguage>("en_EN");
